import { AlertTriangle, CheckCircle2, Crosshair, Link2, LoaderCircle, ShieldCheck } from "lucide-react";

import type { SpecificSatResolution } from "../../lib/types";
import { shortenAddress } from "../../lib/wallets";

const numberFormatter = new Intl.NumberFormat("en-GB");

export interface SpecificSatPickerProps {
  walletConnected: boolean;
  advanced: boolean;
  input: string;
  resolution: SpecificSatResolution | null;
  confirmed: boolean;
  parentRelation: boolean;
  loading: boolean;
  error: string;
  onInput: (value: string) => void;
  onAdvanced: (value: boolean) => void;
  onResolve: () => void;
  onParentRelation: (value: boolean) => void;
  onConfirm: () => void;
}

export function SpecificSatPicker({
  walletConnected,
  advanced,
  input,
  resolution,
  confirmed,
  parentRelation,
  loading,
  error,
  onInput,
  onAdvanced,
  onResolve,
  onParentRelation,
  onConfirm,
}: SpecificSatPickerProps) {
  const canResolve = Boolean(input.trim()) && !loading;
  return <div className="specific-sat-picker" data-testid="specific-sat-picker">
    <div className="specific-sat-input-row">
      <label className="field full">
        <span>{advanced ? "ADVANCED: INSCRIPTION ID OR EXACT SATPOINT" : "SAT NUMBER"}</span>
        <input
          value={input}
          onChange={(event) => onInput(event.target.value)}
          placeholder={advanced ? `${"0".repeat(64)}i0  OR  ${"0".repeat(64)}:0:0` : "2002688242219312"}
          inputMode={advanced ? "text" : "numeric"}
          spellCheck={false}
          autoCapitalize="none"
          autoComplete="off"
        />
        <small>{advanced
          ? "Use an inscription ID or exact txid:vout:offset when you already know the indexed location."
          : "Enter the decimal sat number. We will verify your ordinals wallet and locate the exact sat in its indexed outputs."}</small>
      </label>
      <button className="secondary-button compact" type="button" disabled={!canResolve} onClick={onResolve}>
        {loading ? <LoaderCircle className="spin" /> : <Crosshair />}
        {loading ? "RESOLVING" : walletConnected ? "RESOLVE" : "CONNECT + RESOLVE"}
      </button>
    </div>
    <button className="specific-sat-advanced-toggle" type="button" onClick={() => onAdvanced(!advanced)}>
      {advanced ? "USE SAT NUMBER" : "ADVANCED: USE INSCRIPTION ID OR SATPOINT"}
    </button>
    {error ? <div className="notice error" role="alert"><AlertTriangle size={17} /><span>{error}</span></div> : null}
    {resolution ? <div className={confirmed ? "specific-sat-resolution confirmed" : "specific-sat-resolution"}>
      <header>
        <span><ShieldCheck /><strong>{confirmed ? "SPECIFIC SAT CONFIRMED" : "CHECK THE RESOLVED SAT"}</strong></span>
        <em>{resolution.referenceType === "sat" ? "SAT NUMBER" : resolution.referenceType === "inscription" ? "INSCRIPTION ID" : "EXACT SATPOINT"}</em>
      </header>
      <dl>
        <div><dt>SAT</dt><dd>{numberFormatter.format(resolution.sat)}</dd></div>
        <div><dt>RARITY</dt><dd>{resolution.primaryLabel}</dd></div>
        <div><dt>SAT OFFSET</dt><dd>{numberFormatter.format(resolution.satOffset)}</dd></div>
        <div><dt>CURRENT OUTPUT</dt><dd>{numberFormatter.format(resolution.value)} sats</dd></div>
        <div><dt>GALLERY POSTAGE</dt><dd>{numberFormatter.format(resolution.postageSats)} sats</dd></div>
        <div><dt>CURRENT OWNER</dt><dd title={resolution.address}>{shortenAddress(resolution.address)}</dd></div>
      </dl>
      <div className="specific-sat-identity"><span>CURRENT OWNER ADDRESS</span><code>{resolution.address}</code></div>
      <div className="specific-sat-identity"><span>EXACT SATPOINT</span><code>{resolution.satpoint}</code></div>
      {resolution.existingInscriptionId ? <div className="specific-sat-identity"><span>EXISTING INSCRIPTION</span><code>{resolution.existingInscriptionId}</code></div> : <div className="specific-sat-identity"><span>EXISTING INSCRIPTION</span><strong>NONE ON THIS OUTPUT</strong></div>}
      {resolution.attributes.length ? <div className="specific-sat-attributes">{resolution.attributes.map((attribute) => <span key={attribute.slug}>{attribute.label} ×{numberFormatter.format(attribute.count)}</span>)}</div> : null}
      {resolution.warnings.map((warning) => <p className="specific-sat-warning" key={warning}><AlertTriangle /> <span>{warning}</span></p>)}
      {resolution.existingInscriptionId ? <label className="specific-sat-parent-option">
        <input type="checkbox" checked={parentRelation} disabled={confirmed} onChange={(event) => onParentRelation(event.target.checked)} />
        <span><strong><Link2 /> ALSO ENCODE AN ORD PARENT LINK</strong><small>This spends the existing inscription and adds Ord tag 3, so the new gallery is recognized as its child. Reinscription on the same sat happens whether or not this is selected.</small></span>
      </label> : null}
      {!confirmed ? <button className="primary-button wide" type="button" onClick={onConfirm}><CheckCircle2 /> CONFIRM THIS EXACT SAT</button> : <p className="specific-sat-confirmed"><CheckCircle2 /> This outpoint, offset, sat identity, owner, output value, and parent-link choice will be checked again before signing and broadcasting.</p>}
    </div> : null}
  </div>;
}
